import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  fetchNotificationsForUser,
  notificationSelector,
  registerDeviceForNotifications,
} from "../store/slices/notificationSlice";
import { ErrorToaster } from "../helpers/toasterHelper";
import { isDeviceRegistrationBlockedByFailures } from "../utils/deviceRegistrationLimiter";
import { DEVICE_REGISTRATION_SESSION_KEY, ERROR_MESSAGES, TYPE_CONSTANTS, CHAT_EVENTS } from "../utils/constants";
import { UserNotification } from "../types";
import { getNotificationById } from "../services/notificationService";
import { getNotificationId } from "../utils/deepLink";

interface UseNotificationDeviceLifecycleParams {
  hasToken: boolean;
  userId: string | null | undefined;
}

const NOTIFICATION_POLLING_INTERVAL_MS = 3 * 60 * 1000;
const NOTIFICATION_SYNC_INTERVAL_MS = 15 * 60 * 1000;

export const useNotificationDeviceLifecycle = ({
  hasToken,
  userId,
}: UseNotificationDeviceLifecycleParams) => {
  const dispatch = useAppDispatch();
  const notificationRefreshIntervalRef = useRef<number | null>(null);
  const inboxPollIntervalRef = useRef<number | null>(null);
  const registrationInFlightRef = useRef(false);

  const clearSessionRegistrationMarker = useCallback(() => {
    if (typeof window === TYPE_CONSTANTS.UNDEFINED) {
      return;
    }
    sessionStorage.removeItem(DEVICE_REGISTRATION_SESSION_KEY);
  }, []);

  const isRegisteredForCurrentSession = useCallback((): boolean => {
    if (typeof window === TYPE_CONSTANTS.UNDEFINED || !userId) {
      return false;
    }
    return sessionStorage.getItem(DEVICE_REGISTRATION_SESSION_KEY) === userId;
  }, [userId]);

  const markRegisteredForCurrentSession = useCallback(() => {
    if (typeof window === TYPE_CONSTANTS.UNDEFINED || !userId) {
      return;
    }
    sessionStorage.setItem(DEVICE_REGISTRATION_SESSION_KEY, userId);
  }, [userId]);

  const registerCurrentDevice = useCallback(
    async (forceRefresh: boolean = false) => {
      if (!hasToken || !userId) {
        return;
      }
      if (isDeviceRegistrationBlockedByFailures()) {
        return;
      }
      if (registrationInFlightRef.current || isRegisteredForCurrentSession()) {
        return;
      }

      registrationInFlightRef.current = true;
      try {
        const action = await dispatch(
          registerDeviceForNotifications({ userId, forceRefresh })
        );
        const message = action.payload?.message;
        const skippedBeforeApiCall =
          message === ERROR_MESSAGES.DEVICE_TOKEN_UNAVAILABLE ||
          message === ERROR_MESSAGES.DEVICE_REGISTRATION_BLOCKED;

        if (!skippedBeforeApiCall) {
          markRegisteredForCurrentSession();
        }
      } finally {
        registrationInFlightRef.current = false;
      }
    },
    [
      dispatch,
      hasToken,
      userId,
      isRegisteredForCurrentSession,
      markRegisteredForCurrentSession,
    ]
  );

  useEffect(() => {
    if (!hasToken || !userId) {
      return;
    }
    void registerCurrentDevice();
  }, [hasToken, userId, registerCurrentDevice]);

  useEffect(() => {
    if (!hasToken) {
      registrationInFlightRef.current = false;
      clearSessionRegistrationMarker();
    }
  }, [hasToken, clearSessionRegistrationMarker]);

  useEffect(() => {
    if (
      !hasToken ||
      !userId ||
      typeof window === TYPE_CONSTANTS.UNDEFINED ||
      typeof document === TYPE_CONSTANTS.UNDEFINED
    ) {
      return;
    }

    const syncRegistrationWithBackend = () => {
      void registerCurrentDevice(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === CHAT_EVENTS.VISIBLE) {
        syncRegistrationWithBackend();
      }
    };

    window.addEventListener(CHAT_EVENTS.FOCUS, syncRegistrationWithBackend);
    document.addEventListener(CHAT_EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);
    notificationRefreshIntervalRef.current = window.setInterval(
      () => void registerCurrentDevice(true),
      NOTIFICATION_SYNC_INTERVAL_MS
    );

    let permissionStatus: PermissionStatus | null = null;
    const hasPermissionsApi =
      typeof navigator !== TYPE_CONSTANTS.UNDEFINED && TYPE_CONSTANTS.PERMISSIONS in navigator;
    if (hasPermissionsApi) {
      navigator.permissions
        .query({ name: CHAT_EVENTS.NOTIFICATIONS as PermissionName })
        .then((status) => {
          permissionStatus = status;
          status.onchange = () => {
            void registerCurrentDevice(true);
          };
        })
        .catch(() => { });
    }

    return () => {
      window.removeEventListener(CHAT_EVENTS.FOCUS, syncRegistrationWithBackend);
      document.removeEventListener(CHAT_EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);
      if (notificationRefreshIntervalRef.current) {
        window.clearInterval(notificationRefreshIntervalRef.current);
        notificationRefreshIntervalRef.current = null;
      }
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [hasToken, userId, registerCurrentDevice]);

  const fetchInboxNotificationsSilent = useCallback(() => {
    if (!hasToken || !userId) {
      return;
    }
    dispatch(fetchNotificationsForUser({ userId, silent: true }));
  }, [dispatch, hasToken, userId]);

  useEffect(() => {
    if (!hasToken || !userId || typeof window === TYPE_CONSTANTS.UNDEFINED) {
      return;
    }

    dispatch(fetchNotificationsForUser({ userId, silent: false }));

    inboxPollIntervalRef.current = window.setInterval(
      fetchInboxNotificationsSilent,
      NOTIFICATION_POLLING_INTERVAL_MS
    );

    return () => {
      if (inboxPollIntervalRef.current) {
        window.clearInterval(inboxPollIntervalRef.current);
        inboxPollIntervalRef.current = null;
      }
    };
  }, [dispatch, hasToken, userId, fetchInboxNotificationsSilent]);

  useEffect(() => {
    if (
      !hasToken ||
      !userId ||
      typeof document === TYPE_CONSTANTS.UNDEFINED
    ) {
      return;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === CHAT_EVENTS.VISIBLE) {
        fetchInboxNotificationsSilent();
      }
    };

    document.addEventListener(CHAT_EVENTS.VISIBILITY_CHANGE, onVisibilityChange);
    return () => {
      document.removeEventListener(
        CHAT_EVENTS.VISIBILITY_CHANGE,
        onVisibilityChange
      );
    };
  }, [hasToken, userId, fetchInboxNotificationsSilent]);
};

export const useNotificationDetail = (
  notificationId: string | undefined,
  locationNotification?: UserNotification
) => {
  const { notifications } = useAppSelector(notificationSelector);

  const listNotification = useMemo(() => {
    if (!notificationId) {
      return undefined;
    }
    return notifications.find(
      (row) => getNotificationId(row) === notificationId
    );
  }, [notifications, notificationId]);

  const seedNotification = locationNotification ?? listNotification;
  const [notification, setNotification] = useState<UserNotification | undefined>(
    seedNotification
  );
  const [loading, setLoading] = useState(Boolean(notificationId) && !seedNotification);

  useEffect(() => {
    setNotification(seedNotification);
  }, [seedNotification]);

  useEffect(() => {
    if (!notificationId) {
      return;
    }

    let isMounted = true;
    setLoading((prev) => prev || !seedNotification);

    getNotificationById(notificationId)
      .then((parsed) => {
        if (!isMounted) {
          return;
        }
        setNotification(parsed);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        if (!seedNotification) {
          ErrorToaster(ERROR_MESSAGES.FAILED_TO_FETCH_NOTIFICATION);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [notificationId, seedNotification]);

  return { notification, loading };
};
